import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import mixpanel from "mixpanel-browser";
import { useSelector } from "react-redux";
import appConfig from "../../features/app/configuration/AppConfig";
import { RootState } from "../../features/app/Store";
import { UserInitializationState } from "../../features/user/Models";
import { MultiSitePage, SitePage } from "../../pages/PageNames";
import { LoadingState } from "../../features/app/LoadingState";
import { ProductType } from "../../features/purchasedProducts/Models";

export const getMixpanelPageName = (pathname: string): string => {
  const [firstSegment, secondSegment] = pathname.split("/").filter((segment) => segment !== "");

  if (firstSegment && firstSegment in MultiSitePage) {
    return firstSegment;
  }

  if (secondSegment && secondSegment in SitePage) {
    return secondSegment;
  }

  return secondSegment ?? firstSegment ?? "Unknown";
};

export const useMixpanel = () => {
  const location = useLocation();
  const userData = useSelector((state: RootState) => state.user);
  const sitesMetadata = useSelector((state: RootState) => state.multiSitesMetadata);
  const sitesPurchasedProducts = useSelector(
    (state: RootState) => state.purchasedProducts?.productsBySite
  );
  const isMixpanelInitialized = useRef(false);
  const lastTrackedPathRef = useRef<string | null>(null);
  const enrichedPageViewEvent = "Enriched Page View";
  const sitePropertiesKeys = [
    "siteId",
    "siteCode",
    "displayName",
    "siteCountry",
    "customerId",
    "contractType",
    "numberOfTrackers",
    "siteStage",
    "acCapacityKiloWattActual",
    "purchasedProducts",
  ] as const;

  type SiteKey = (typeof sitePropertiesKeys)[number];
  type SiteProperties = {
    [K in SiteKey]: unknown;
  };

  function convertProductTypesToStrings(productTypes: ProductType[]): string[] {
    return productTypes.map((productType) => ProductType[productType]);
  }

  function getFirstSegment(path: string | null): string | undefined {
    return path?.split("/").filter((segment) => segment !== "")[0];
  }
  function clearSiteProperties() {
    sitePropertiesKeys.forEach((key) => mixpanel.unregister(key));
  }

  function registerSiteProperties(siteProperties: SiteProperties) {
    mixpanel.register(siteProperties);
  }

  const mixpanelInit = () => {
    if (isMixpanelInitialized.current || !appConfig.mixpanelProjectId) {
      return;
    }

    try {
      mixpanel.init(appConfig.mixpanelProjectId, {
        api_host: "https://api-eu.mixpanel.com",
        autocapture: {
          capture_text_content: true,
        },
        // Page views are tracked below after route and site context are available.
        track_pageview: false,
        persistence: "localStorage",
      });
      isMixpanelInitialized.current = true;
    } catch (e) {
      console.error("Failed to initialize Mixpanel:", e);
    }
  };

  const mixpanelIdentifyUser = () => {
    if (
      isMixpanelInitialized.current &&
      userData.initializationState === UserInitializationState.Initialized &&
      userData.oid
    ) {
      mixpanel.identify(userData.oid);
      mixpanel.people.set({ $name: userData.userName });
    }
  };

  const mixpanelEnrichAndTrackPageView = () => {
    if (!isMixpanelInitialized.current) {
      return;
    }

    const newPath = location.pathname;
    if (lastTrackedPathRef.current === newPath) {
      return;
    }

    const newSegment = getFirstSegment(newPath);
    const lastTrackedSegment = getFirstSegment(lastTrackedPathRef.current);

    const trackPageView = () => {
      mixpanel.track_pageview(
        {
          page: newPath,
          pageName: getMixpanelPageName(newPath),
        },
        { event_name: enrichedPageViewEvent }
      );
      lastTrackedPathRef.current = newPath;
    };

    if (lastTrackedSegment && newSegment === lastTrackedSegment) {
      // same segment - track without additional enrichment
      trackPageView();
      return;
    }

    if (!newSegment || newSegment in MultiSitePage) {
      // Multi-site: clear site specific props and track
      clearSiteProperties();
      trackPageView();
      return;
    }

    if (sitesMetadata.loading !== LoadingState.Complete) {
      return;
    }

    const siteId = sitesMetadata.siteCodeToIdMap[newSegment];
    if (siteId) {
      const siteMetadata = sitesMetadata.sites[siteId];
      registerSiteProperties({
        siteId: siteMetadata.siteId,
        siteCode: siteMetadata.siteCode,
        displayName: siteMetadata.displayName,
        acCapacityKiloWattActual: siteMetadata.acCapacityKiloWatt,
        purchasedProducts: sitesPurchasedProducts?.[siteId]
          ? convertProductTypesToStrings(sitesPurchasedProducts[siteId])
          : [],
        numberOfTrackers: siteMetadata.numberOfTrackers,
        siteCountry: siteMetadata.address.country,
        siteStage: siteMetadata.businessInfo.siteStage,
        contractType: siteMetadata.businessInfo.contractType,
        customerId: siteMetadata.businessInfo.customerId,
      });
    } else {
      clearSiteProperties();
    }
    trackPageView();
  };

  useEffect(mixpanelInit, []);
  useEffect(mixpanelIdentifyUser, [userData]);
  useEffect(mixpanelEnrichAndTrackPageView, [location, sitesMetadata, sitesPurchasedProducts]);
};
